/**
 * 实战回测(滚动模拟)
 *
 * 模拟:从某期开始,每期用历史生成池子,看是否命中下一期
 * 滚动 200 期,统计:
 *   - 总命中率
 *   - 平均信心
 *   - 高信心期数
 *   - 累计盈亏
 */

import { generateDynamicPool, getConfidenceAdvice } from './dynamicPool.js';
import { predictAll } from './mlPredict.js';
import * as formulas from './formulas.js';

/**
 * 滚动回测
 * @param {Array} history - 历史开奖(从老到新)
 * @param {Object} options
 * @param {number} options.startIdx - 起始索引(默认 200)
 * @param {number} options.count - 模拟期数(默认 200)
 * @param {Set} options.userKills
 * @param {Set} options.userKillPairs
 * @returns {Object} { results, summary }
 */
export function rollingBacktest(history, options = {}) {
  const { startIdx = Math.max(0, history.length - 200), count = 200, userKills = new Set(), userKillPairs = new Set() } = options;
  const endIdx = Math.min(startIdx + count, history.length - 1);
  if (endIdx <= startIdx + 30) {
    return { error: '历史数据不足' };
  }

  const results = [];
  let hits = 0;
  let highConfHits = 0;
  let highConfCount = 0;
  let totalBet = 0;
  let totalWin = 0;

  for (let i = startIdx; i < endIdx; i++) {
    // 训练用历史(之前的所有期)
    const trainHistory = history.slice(0, i);
    if (trainHistory.length < 30) continue;

    // 实际开奖
    const actual = history[i];
    const actualCode = String(actual.code || actual.number || '').padStart(3, '0');
    if (!actualCode || actualCode.length !== 3) continue;

    // 生成池子
    const result = generateDynamicPool({
      recent: trainHistory,
      userKills,
      userKillPairs,
    });
    if (result.error) continue;

    const hit = result.pool.includes(actualCode);
    const advice = getConfidenceAdvice(result.confidence);
    if (hit) hits++;
    if (hit && result.confidence >= 0.7) highConfHits++;
    if (result.confidence >= 0.7) highConfCount++;

    // 假设组六赔率 1:160(2 元中 320 元)
    const unitPrice = 2;
    const bet = result.size * unitPrice;
    const win = hit ? 320 : 0;  // 组六奖金(固定)
    totalBet += bet;
    totalWin += win;

    results.push({
      idx: i,
      period: actual.period || `第 ${i + 1} 期`,
      actual: actualCode,
      hit,
      confidence: result.confidence,
      confidence_level: advice.level,
      pool_size: result.size,
      bet,
      win,
      profit: win - bet,
    });
  }

  const totalRounds = results.length;
  const summary = {
    total_rounds: totalRounds,
    hits,
    hit_rate: totalRounds > 0 ? hits / totalRounds : 0,
    high_confidence_rounds: highConfCount,
    high_confidence_hit_rate: highConfCount > 0 ? highConfHits / highConfCount : 0,
    high_confidence_share: totalRounds > 0 ? highConfCount / totalRounds : 0,
    avg_confidence: totalRounds > 0 ? results.reduce((s, r) => s + r.confidence, 0) / totalRounds : 0,
    avg_pool_size: totalRounds > 0 ? results.reduce((s, r) => s + r.pool_size, 0) / totalRounds : 0,
    total_bet: totalBet,
    total_win: totalWin,
    total_profit: totalWin - totalBet,
    roi: totalBet > 0 ? (totalWin - totalBet) / totalBet : 0,
    by_level: {
      high: {
        count: results.filter(r => r.confidence_level === 'high').length,
        hits: results.filter(r => r.confidence_level === 'high' && r.hit).length,
      },
      medium: {
        count: results.filter(r => r.confidence_level === 'medium').length,
        hits: results.filter(r => r.confidence_level === 'medium' && r.hit).length,
      },
      low: {
        count: results.filter(r => r.confidence_level === 'low').length,
        hits: results.filter(r => r.confidence_level === 'low' && r.hit).length,
      },
    },
  };

  return { results, summary };
}

/**
 * 对比回测:固定池子 vs 动态池子 vs L4
 */
export function compareStrategies(history, options = {}) {
  const baseline = rollingBacktest(history, options);

  // 固定 L2 500 注
  const fixedL2Results = [];
  for (let i = (options.startIdx || Math.max(0, history.length - 200)); i < Math.min((options.startIdx || 0) + (options.count || 200), history.length - 1); i++) {
    const trainHistory = history.slice(0, i);
    if (trainHistory.length < 30) continue;
    const actual = history[i];
    const actualCode = String(actual.code || actual.number || '').padStart(3, '0');
    if (!actualCode || actualCode.length !== 3) continue;
    // 简化:用训练集 ML 概率 top 500
    let mlProbs = {};
    try {
      const mlRes = predictAll(trainHistory);
      if (Array.isArray(mlRes)) {
        mlRes.forEach((p, j) => { mlProbs[String(j).padStart(3, '0')] = p; });
      }
    } catch (e) {}
    const ranked = Object.entries(mlProbs).sort((a, b) => b[1] - a[1]).slice(0, 500);
    const pool = ranked.map(([k]) => k);
    const hit = pool.includes(actualCode);
    fixedL2Results.push({ hit, bet: 500 * 2, win: hit ? 320 : 0 });
  }
  const fixedL2Hits = fixedL2Results.filter(r => r.hit).length;
  const fixedL2Total = fixedL2Results.length;
  const fixedL2 = {
    name: '固定 L2 500 注',
    total_rounds: fixedL2Total,
    hits: fixedL2Hits,
    hit_rate: fixedL2Total > 0 ? fixedL2Hits / fixedL2Total : 0,
    total_bet: fixedL2Results.reduce((s, r) => s + r.bet, 0),
    total_win: fixedL2Results.reduce((s, r) => s + r.win, 0),
    profit: fixedL2Results.reduce((s, r) => s + r.win - r.bet, 0),
  };

  // L4 100 注(ML top 100)
  const l4Results = [];
  for (let i = (options.startIdx || Math.max(0, history.length - 200)); i < Math.min((options.startIdx || 0) + (options.count || 200), history.length - 1); i++) {
    const trainHistory = history.slice(0, i);
    if (trainHistory.length < 30) continue;
    const actual = history[i];
    const actualCode = String(actual.code || actual.number || '').padStart(3, '0');
    if (!actualCode || actualCode.length !== 3) continue;
    let mlProbs = {};
    try {
      const mlRes = predictAll(trainHistory);
      if (Array.isArray(mlRes)) {
        mlRes.forEach((p, j) => { mlProbs[String(j).padStart(3, '0')] = p; });
      }
    } catch (e) {}
    const ranked = Object.entries(mlProbs).sort((a, b) => b[1] - a[1]).slice(0, 100);
    const pool = ranked.map(([k]) => k);
    const hit = pool.includes(actualCode);
    l4Results.push({ hit, bet: 100 * 2, win: hit ? 320 : 0 });
  }
  const l4Hits = l4Results.filter(r => r.hit).length;
  const l4Total = l4Results.length;
  const l4 = {
    name: '固定 L4 100 注',
    total_rounds: l4Total,
    hits: l4Hits,
    hit_rate: l4Total > 0 ? l4Hits / l4Total : 0,
    total_bet: l4Results.reduce((s, r) => s + r.bet, 0),
    total_win: l4Results.reduce((s, r) => s + r.win, 0),
    profit: l4Results.reduce((s, r) => s + r.win - r.bet, 0),
  };

  return {
    dynamic: baseline.summary,
    fixed_l2: fixedL2,
    l4: l4,
  };
}

/**
 * 凯利公式下注计算
 *
 * f* = (b × p - q) / b
 *   b = 赔率(中奖金额/投注金额,不含本金)
 *   p = 命中率
 *   q = 1 - p(失败概率)
 *
 * 中国体彩 3D 组六:
 *   - 2 元/注,中奖 320 元
 *   - 赔率 b = 320/2 = 160(纯利)/2 = 160?实际 b 是 净赔率(中奖/投注 - 1) = 159
 *   - 简化:b = 160
 */
export function kellyBet({
  hit_rate,           // p (0-1)
  odds = 160,         // b 赔率
  bankroll,           // 总资金
  maxBetPct = 0.20,   // 单期最大注码比例(防爆仓)
  fractional = 0.5,   // 分数凯利(0.5 = 半凯利,更稳)
}) {
  const p = hit_rate;
  const q = 1 - p;
  const b = odds;
  // 凯利公式
  let f = (b * p - q) / b;
  if (f < 0) f = 0;  // 负数不要下注
  // 分数凯利
  f = f * fractional;
  // 上限
  f = Math.min(f, maxBetPct);
  // 期望净利率
  const expectedReturn = p * b - q;
  // 每注 2 元
  const unit = 2;
  const betAmount = Math.floor((bankroll * f) / unit) * unit;
  const betCount = betAmount / unit;

  return {
    f_kelly: f,
    bet_amount: betAmount,
    bet_count: betCount,
    expected_return: expectedReturn,
    expected_profit_per_bet: expectedReturn * betAmount,
    advice: f < 0.01
      ? '凯利 f < 1%,不要下注(长期负期望)'
      : f < 0.05
        ? '轻仓试水'
        : f < 0.15
          ? '标准下注'
          : '重仓出击(注意风险)',
  };
}

/**
 * 模拟凯利长期表现
 */
export function simulateKelly(bankroll, hits, total, odds = 160, fractional = 0.5) {
  const p = total > 0 ? hits / total : 0;
  const kelly = kellyBet({ hit_rate: p, odds, bankroll, fractional });
  const f = kelly.f_kelly;
  const unit = 2;

  // 模拟 100 期
  const periods = 100;
  let cash = bankroll;
  const trace = [];
  for (let i = 0; i < periods; i++) {
    const bet = Math.floor((cash * f) / unit) * unit;
    if (bet < unit) break;
    const hit = Math.random() < p;
    cash -= bet;
    if (hit) cash += bet * (odds / 1);  // 简化:中奖得 bet × odds(注意:赔率包含本金)
    trace.push({ period: i + 1, hit, bet, balance: cash });
  }
  return {
    initial: bankroll,
    final: cash,
    profit: cash - bankroll,
    roi: (cash - bankroll) / bankroll,
    f,
    trace,
  };
}
